'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { BaseModal } from './BaseModal';
import { fetchDaemonTriggerById } from '../lib/api/daemon';
import { getAuthSession } from '../lib/authSession';
import { useMeRecentRuns } from '../hooks/queries/useMeRecentRuns';
import type { MeActiveRun } from '../lib/api/me';
import type { PendingAttachmentDraft } from '../types/attachment';
import { RecentRunsList } from './home/RecentRunsList';

const OPEN_FOLLOW_UP_PICKER_EVENT = 'openRunnerRequestFollowUpPicker';
const OPEN_DAEMON_TRIGGER_MODAL_EVENT = 'openDaemonTriggerModal';

type OpenDetail = {
  attachmentDrafts: PendingAttachmentDraft[];
  description: string;
};

/**
 * 데스크탑 캡처(여러 건 가능)를 기존 Runner request에 이어쓰기 위해 대상 request를 선택하는 picker.
 *
 * UI: 홈의 최근 러너 위젯과 동일한 chain 그룹화 카드 리스트(`RecentRunsList`)를 재사용.
 *
 * 트리거: 글로벌 CustomEvent `openRunnerRequestFollowUpPicker` (DesktopCaptureChip이 dispatch)
 * 선택 시 fetchDaemonTriggerById로 권한 검증된 trigger를 가져온 뒤 'openDaemonTriggerModal'
 * (mode='continue')에 prefillAttachmentDrafts를 동행해 dispatch.
 */
export function RunnerRequestFollowUpPicker(): React.JSX.Element | null {
  const t = useTranslations('components.appLayout');
  const tCommon = useTranslations('common');
  const [isOpen, setIsOpen] = useState(false);
  const [detail, setDetail] = useState<OpenDetail | null>(null);
  const [busyTriggerId, setBusyTriggerId] = useState<string | null>(null);

  const session = getAuthSession();
  const { data: runs = [], isLoading } = useMeRecentRuns(session?.token ?? null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event): void => {
      const ce = event as CustomEvent<OpenDetail>;
      if (!ce.detail?.attachmentDrafts || ce.detail.attachmentDrafts.length === 0) return;
      setDetail(ce.detail);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_FOLLOW_UP_PICKER_EVENT, handler);
    return () => window.removeEventListener(OPEN_FOLLOW_UP_PICKER_EVENT, handler);
  }, []);

  const close = (): void => {
    setIsOpen(false);
    setDetail(null);
    setBusyTriggerId(null);
  };

  const onSelect = async (run: MeActiveRun): Promise<void> => {
    if (!detail || !session?.token) return;
    setBusyTriggerId(run.id);
    try {
      const trigger = await fetchDaemonTriggerById(session.token, run.id);
      window.dispatchEvent(
        new CustomEvent(OPEN_DAEMON_TRIGGER_MODAL_EVENT, {
          detail: {
            mode: 'continue',
            sourceTrigger: trigger,
            prefillAttachmentDrafts: detail.attachmentDrafts,
          },
        }),
      );
      close();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('followUpPickerFetchError');
      toast.error(t('followUpPickerErrorTitle'), { description: message });
      setBusyTriggerId(null);
    }
  };

  if (!isOpen || !detail) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={close} size="2xl">
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 className="min-w-0 text-base font-semibold text-base-content">{t('followUpPickerTitle')}</h2>
          <button
            type="button"
            className="shrink-0 rounded-lg border border-base-200 px-2.5 py-1 text-xs text-[var(--color-fg-muted)] transition hover:bg-base-200"
            onClick={close}
            aria-label={tCommon('actions.close')}
          >
            {tCommon('actions.close')}
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <RecentRunsList
            runs={runs}
            isLoading={isLoading}
            onSelect={(run) => void onSelect(run)}
            busyRunId={busyTriggerId}
          />
        </div>
      </div>
    </BaseModal>
  );
}
